import axios, { type AxiosError, type AxiosResponse } from 'axios';
import { AUTH_ENDPOINTS } from '@/url_constants/authEndpoints';
import type {
  AuthError,
  GenerateAllOtpRequest,
  GenerateAllOtpResponse,
  LoginRequest,
  LoginResponse,
  OtpGenerateRequest,
  OtpResponse,
  OtpVerifyRequest,
  RegisterRequest,
  RegisterResponse,
  VerifyOtpRequest,
  VerifyOtpResponse,
} from '@/types/auth.types';

const API_BASE_URL =
  import.meta.env.VITE_API_BASE_URL || 'https://banking-dev.devopsark.com/api/v1';

const apiClient = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  timeout: 30000,
});

const extractErrorMessage = (
  error: AxiosError<Record<string, unknown>>,
  fallbackMessage: string
): AuthError => {
  const response = error.response;
  if (response?.data && typeof response.data === 'object') {
    const message =
      typeof response.data.message === 'string'
        ? response.data.message
        : typeof response.data.error === 'string'
          ? response.data.error
          : fallbackMessage;
    return { message, code: String(response.data.code || response.status) };
  }
  return { message: error.message || fallbackMessage };
};

export const registerUser = async (
  payload: RegisterRequest
): Promise<RegisterResponse> => {
  try {
    const response: AxiosResponse<RegisterResponse> = await apiClient.post(
      AUTH_ENDPOINTS.REGISTER,
      payload
    );
    return response.data;
  } catch (error) {
    const axiosError = error as AxiosError<Record<string, unknown>>;
    const authError = extractErrorMessage(
      axiosError,
      'Registration failed. Please try again.'
    );
    throw new Error(authError.message);
  }
};

export const generateAllOtp = async (
  payload: GenerateAllOtpRequest
): Promise<GenerateAllOtpResponse> => {
  try {
    const response: AxiosResponse<GenerateAllOtpResponse> =
      await apiClient.post(AUTH_ENDPOINTS.GENERATE_ALL_OTP, payload);
    return response.data;
  } catch (error) {
    const axiosError = error as AxiosError<Record<string, unknown>>;
    const authError = extractErrorMessage(
      axiosError,
      'Failed to send OTP. Please try again.'
    );
    throw new Error(authError.message);
  }
};

export const verifyRegisterOtp = async (
  payload: VerifyOtpRequest
): Promise<VerifyOtpResponse> => {
  try {
    const response: AxiosResponse<VerifyOtpResponse> = await apiClient.post(
      AUTH_ENDPOINTS.VERIFY_OTP,
      payload
    );
    return response.data;
  } catch (error) {
    const axiosError = error as AxiosError<Record<string, unknown>>;
    const authError = extractErrorMessage(
      axiosError,
      'OTP verification failed. Please try again.'
    );
    throw new Error(authError.message);
  }
};

export const loginUser = async (
  payload: LoginRequest
): Promise<LoginResponse> => {
  try {
    const response: AxiosResponse<LoginResponse> = await apiClient.post(
      AUTH_ENDPOINTS.LOGIN,
      payload
    );
    return response.data;
  } catch (error) {
    const axiosError = error as AxiosError<Record<string, unknown>>;
    const authError = extractErrorMessage(
      axiosError,
      'Login failed. Please try again.'
    );
    throw new Error(authError.message);
  }
};

export const generateOtp = async (
  payload: OtpGenerateRequest
): Promise<OtpResponse> => {
  try {
    const response: AxiosResponse<OtpResponse> = await apiClient.post(
      AUTH_ENDPOINTS.GENERATE_LOGIN_OTP,
      payload
    );
    return response.data;
  } catch (error) {
    const axiosError = error as AxiosError<Record<string, unknown>>;
    const authError = extractErrorMessage(
      axiosError,
      'Failed to generate OTP. Please try again.'
    );
    throw new Error(authError.message);
  }
};

export const verifyLoginOtp = async (
  payload: OtpVerifyRequest
): Promise<OtpResponse> => {
  try {
    const response: AxiosResponse<OtpResponse> = await apiClient.post(
      AUTH_ENDPOINTS.VERIFY_LOGIN_OTP,
      payload
    );
    return response.data;
  } catch (error) {
    const axiosError = error as AxiosError<Record<string, unknown>>;
    const authError = extractErrorMessage(
      axiosError,
      'OTP verification failed. Please try again.'
    );
    throw new Error(authError.message);
  }
};
